import { Button, Dialog, Text } from '@/components/DesignSystem';
import { UserSyncEndpoint } from '@api/endpoints/UserSyncEndpoint';
import { Stack } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

type Keep = UserSyncEndpoint.KeepPhone.Request['keep'];
type Props = {
  onChoose: (keep: Keep) => void,
  onClose: () => void,
  row: UserSyncEndpoint.Row | null,
};

const PhoneLine = ({ label, note, phone }: { label: string, note?: string, phone: string }) => (
  <Stack gap={0.5}>
    <Text category="title-small">{label}</Text>
    <Text>{phone}</Text>
    {note && <Text category="body-small" sx={{ color: 'text.secondary' }}>{note}</Text>}
  </Stack>
);

/** A linked LP account and its sign-in have different phones: the admin says which is right. */
const PhoneChoiceDialog = ({ onChoose, onClose, row }: Props) => {
  const { t } = useTranslation();
  const phones = row?.phones;
  const noPhone = t(($) => $.userSync.phone.none);

  const signInNoteOf = (choice: UserSyncEndpoint.PhoneChoice | undefined) => {
    if (choice?.signInPhoneTaken) {
      return t(($) => $.userSync.phone.takenByOtherAccount);
    }
    return choice?.fromSignIn ? undefined : t(($) => $.userSync.phone.lpCannotUse);
  };

  return (
    <Dialog
      acceptButton={(props) => (
        <Stack direction="row" gap={1}>
          <Button
            disabled={!phones?.fromLp}
            onClick={() => onChoose('lp')}
            testID="user-sync-phone-keep-lp"
            variant="outlined"
          >
            {t(($) => $.userSync.phone.keepLp)}
          </Button>
          <Button {...props} disabled={!phones?.fromSignIn} onClick={() => onChoose('sign-in')}>
            {t(($) => $.userSync.phone.keepSignIn)}
          </Button>
        </Stack>
      )}
      cancelButton={(props) => (
        <Button {...props} onClick={onClose}>{t(($) => $.userSync.phone.cancel)}</Button>
      )}
      maxWidth="sm"
      onClose={onClose}
      open={!!phones}
      testID="user-sync-phone"
      title={t(($) => $.userSync.phone.title)}
    >
      <Stack gap={2}>
        <PhoneLine
          label={t(($) => $.userSync.phone.lp)}
          note={phones?.fromLp
            ? t(($) => $.userSync.phone.lpUnverified)
            : t(($) => $.userSync.phone.noLpPhone)}
          phone={row?.account?.telephone ?? noPhone}
        />
        <PhoneLine
          label={t(($) => $.userSync.phone.signIn)}
          note={signInNoteOf(phones)}
          phone={row?.person?.phone ?? noPhone}
        />
        <Text sx={{ color: 'text.secondary' }}>{t(($) => $.userSync.phone.signInNote)}</Text>
      </Stack>
    </Dialog>
  );
};

export default PhoneChoiceDialog;
